import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import AdminAuditPanel from './AdminAuditPanel'
import AdminEarningsPanel from './AdminEarningsPanel'
import AdminGlobalSearch from './AdminGlobalSearch'
import AdminImeiPanel from './AdminImeiPanel'
import AdminListingsPanel from './AdminListingsPanel'
import AdminOverviewPanel from './AdminOverviewPanel'
import AdminPhoneModelsPanel from './AdminPhoneModelsPanel'
import AdminTransactionsPanel from './AdminTransactionsPanel'
import AdminUsersPanel from './AdminUsersPanel'
import type {
  AdminActivityEvent,
  AdminBadges,
  AdminEarningsSummary,
  AdminHealth,
  AdminOverview,
  AdminSection,
  AdminTrends,
} from './adminTypes'

type Props = {
  token: string
  onLastUpdated?: (date: Date) => void
}

type NavItem = {
  key: AdminSection
  icon: string
  badgeKeys?: Array<keyof AdminBadges>
}

const NAV: NavItem[] = [
  { key: 'overview', icon: '◫' },
  { key: 'users', icon: '◉' },
  { key: 'listings', icon: '▣', badgeKeys: ['listings_above_ai', 'listings_flagged'] },
  { key: 'models', icon: '▤' },
  { key: 'imei', icon: '◎' },
  { key: 'transactions', icon: '◈', badgeKeys: ['stuck_transactions'] },
  { key: 'earnings', icon: '€' },
  { key: 'audit', icon: '◷' },
]

export default function AdminShell({ token, onLastUpdated }: Props) {
  const { t } = useTranslation()
  const [section, setSection] = useState<AdminSection>('overview')
  const [overview, setOverview] = useState<AdminOverview | null>(null)
  const [earnings, setEarnings] = useState<AdminEarningsSummary | null>(null)
  const [trends, setTrends] = useState<AdminTrends | null>(null)
  const [activity, setActivity] = useState<AdminActivityEvent[]>([])
  const [health, setHealth] = useState<AdminHealth | null>(null)
  const [badges, setBadges] = useState<AdminBadges | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [listingFilter, setListingFilter] = useState<'' | 'above_ai' | 'no_photos' | 'flagged' | 'hidden'>('')
  const [openListingId, setOpenListingId] = useState<number | null>(null)
  const [openTransactionId, setOpenTransactionId] = useState<number | null>(null)
  const [transactionFilter, setTransactionFilter] = useState<'all' | 'stuck'>('all')
  const [openUserId, setOpenUserId] = useState<number | null>(null)

  const loadBadges = useCallback(() => {
    adminAPI.badges(token).then((res) => setBadges(res.data as AdminBadges)).catch(() => {})
  }, [token])

  const loadOverview = useCallback(() => {
    setLoading(true)
    setError('')
    Promise.all([
      adminAPI.overview(token),
      adminAPI.earningsSummary(token),
      adminAPI.trends(token, { days: 30 }),
      adminAPI.activityFeed(token, { minutes: 20 }),
      adminAPI.health(token),
    ])
      .then(([ov, er, tr, act, hl]) => {
        setOverview(ov.data as AdminOverview)
        setEarnings(er.data as AdminEarningsSummary)
        setTrends(tr.data as AdminTrends)
        setActivity((act.data?.items as AdminActivityEvent[]) ?? [])
        setHealth(hl.data as AdminHealth)
        const now = new Date()
        setLastUpdated(now)
        onLastUpdated?.(now)
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, onLastUpdated])

  useEffect(() => {
    loadBadges()
  }, [loadBadges])

  useEffect(() => {
    if (section === 'overview') loadOverview()
  }, [section, loadOverview])

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadBadges()
      if (section === 'overview') loadOverview()
    }, 60_000)
    return () => window.clearInterval(timer)
  }, [section, loadOverview, loadBadges])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearchOpen(true)
      }
      if (event.key === 'Escape') setSearchOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  function navBadgeCount(item: NavItem): number {
    if (!badges || !item.badgeKeys) return 0
    return item.badgeKeys.reduce((sum, key) => sum + (badges[key] ?? 0), 0)
  }

  function navigate(section: AdminSection, hint?: string) {
    setSection(section)
    if (section === 'listings') {
      if (hint === 'above_ai') setListingFilter('above_ai')
      else if (hint) setListingFilter(hint as typeof listingFilter)
    }
    if (section === 'transactions' && hint === 'stuck') {
      setTransactionFilter('stuck')
    }
  }

  function handleSearchNavigate(
    target: AdminSection,
    entity?: { type: 'user' | 'listing' | 'imei'; id: number },
  ) {
    setSection(target)
    if (entity?.type === 'listing') {
      setOpenListingId(entity.id)
      setSection('listings')
    } else if (entity?.type === 'user') {
      setOpenUserId(entity.id)
      setSection('users')
    } else if (entity?.type === 'imei') {
      setSection('imei')
    }
  }

  return (
    <div className="vc-admin-layout">
      <aside className="vc-admin-sidebar" aria-label={t('admin.title')}>
        <p className="vc-admin-sidebar__brand">{t('admin.brandTitle')}</p>
        <button type="button" className="vc-admin-sidebar__search" onClick={() => setSearchOpen(true)}>
          {t('admin.globalSearch')} <kbd>⌘K</kbd>
        </button>
        <nav className="vc-admin-sidebar__nav">
          {NAV.map((item) => {
            const count = navBadgeCount(item)
            return (
              <button
                key={item.key}
                type="button"
                className={`vc-admin-sidebar__link${section === item.key ? ' is-active' : ''}`}
                onClick={() => setSection(item.key)}
              >
                <span className="vc-admin-sidebar__icon" aria-hidden="true">
                  {item.icon}
                </span>
                <span>{t(`admin.nav.${item.key}`)}</span>
                {count > 0 ? <span className="vc-admin-sidebar__badge">{count}</span> : null}
              </button>
            )
          })}
        </nav>
      </aside>

      <main className="vc-admin-main">
        {section === 'overview' ? (
          <AdminOverviewPanel
            token={token}
            overview={overview}
            earnings={earnings}
            trends={trends}
            activity={activity}
            health={health}
            loading={loading}
            error={error}
            lastUpdated={lastUpdated}
            onRefresh={() => {
              loadOverview()
              loadBadges()
            }}
            onNavigate={navigate}
            onOpenTransaction={(id) => {
              setOpenTransactionId(id)
              setSection('transactions')
            }}
            onOpenUser={(userId) => {
              setOpenUserId(userId)
              setSection('users')
            }}
          />
        ) : null}
        {section === 'users' ? (
          <AdminUsersPanel token={token} initialOpenUserId={openUserId} onConsumedInitialUser={() => setOpenUserId(null)} />
        ) : null}
        {section === 'listings' ? (
          <AdminListingsPanel
            token={token}
            initialFilter={listingFilter}
            initialOpenId={openListingId}
            onOpenUser={(userId) => {
              setOpenUserId(userId)
              setSection('users')
            }}
          />
        ) : null}
        {section === 'models' ? <AdminPhoneModelsPanel token={token} /> : null}
        {section === 'imei' ? <AdminImeiPanel token={token} /> : null}
        {section === 'transactions' ? (
          <AdminTransactionsPanel
            token={token}
            initialOpenId={openTransactionId}
            initialFilter={transactionFilter}
          />
        ) : null}
        {section === 'earnings' ? <AdminEarningsPanel token={token} /> : null}
        {section === 'audit' ? <AdminAuditPanel token={token} /> : null}
      </main>

      <AdminGlobalSearch
        token={token}
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={handleSearchNavigate}
      />
    </div>
  )
}
